import { useBLoC } from 'hooks/useBLoC';
import { useInitBloc } from 'hooks/useInitBloc';
import { BLoCBase, BLoCParams, renderBlocChild, IBLoCInitialisable } from 'types/BLoCBase';
import React from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useQueryParams, NumberParam, StringParam } from 'hooks/useQueryParams';
import { $get } from 'services/api';
import { finalize, map } from 'rxjs';
import { toast } from 'services/toast';
import { LoginPageTypes } from './LoginPage.types';
import { navigate } from 'services/navigate';

type State = {
  showErrors?: boolean;
  ssoLoading?: boolean;
  ssoProviders: LoginPageTypes.SSOProvider[] | null;
};

type FormValues = {
  email: string;
  password: string;
  remember: boolean;
};

class BLoC extends BLoCBase<State> implements IBLoCInitialisable {
  public $showErrors = this.$getState('showErrors');
  public $ssoProviders = this.$getState('ssoProviders');
  public $ssoLoading = this.$getState('ssoLoading');
  public $showMainLogin = this.$getState('ssoProviders').pipe(map((p) => !p?.length));

  constructor(
    public readonly form: UseFormReturn<FormValues, object>,
    public readonly loginFailed?: boolean,
    public readonly lockout?: string
  ) {
    super({
      ssoProviders: [],
    });
  }

  public onInit = () => {
    if (this.loginFailed) this.setShowError(true);
    // Remove url params after read
    navigate(window.location.pathname, { replace: true });
  };

  public setShowError = (value: boolean) => this.setState('showErrors', value);

  public ssoLogin = () => {
    this.setState('ssoLoading', true);
    this.addSub(
      $get<LoginPageTypes.SSOProvider[]>('sso/saml2-tenants')
        .pipe(finalize(() => this.setState('ssoLoading', false)))
        .subscribe({
          next: (data) => {
            this.setState('ssoProviders', data);
            navigate(window.location.pathname, { replace: true });
          },
          error: (err) =>
            toast({
              severity: 'error',
              summary: err?.message || 'An error has ocurred.',
            }),
        })
    );
  };

  public back = () => this.setState('ssoProviders', []);
}

const Context = React.createContext<Readonly<BLoC>>({} as any);

export const useLoginPageBLoC = () => useBLoC<BLoC>(Context);

export const LoginPageBLoC: React.FC<BLoCParams<BLoC, State>> = ({ children }) => {
  const [queryParams] = useQueryParams({
    'login-failed': NumberParam,
    lockout: StringParam,
    email: StringParam,
  });
  const form = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      email: queryParams.email || '',
      password: '',
      remember: false,
    },
  });

  const bloc = useInitBloc(() => new BLoC(form, !!queryParams['login-failed'], queryParams.lockout));
  return bloc ? <Context.Provider value={bloc}>{renderBlocChild(children, bloc)}</Context.Provider> : null;
};
